import type { CampaignManagementApi } from '~/services/campaign-management-api/campaign-management-api.types';

import type {
  Campaign,
  CampaignDetail,
  CampaignFilters,
  CampaignKpis,
  CampaignStatus,
  DetailKpi,
  FilterSelectOption,
  IdentificationData,
  PeriodData,
  SegmentationData,
} from '../types';

import { formatCampaignDate, formatNumber, formatPct, toCampaignPeriodBoundaryIso } from '../utils';

const STATUS_FROM_API: Record<CampaignManagementApi.CampaignListStatus, CampaignStatus> = {
  ACTIVE: 'active',
  CANCELED: 'canceled',
  DRAFT: 'draft',
  FINISHED: 'finished',
  PAUSED: 'paused',
  PROCESSING: 'processing',
  SCHEDULED: 'scheduled',
};

const STATUS_TO_API: Record<CampaignStatus, CampaignManagementApi.CampaignListStatus> = {
  active: 'ACTIVE',
  canceled: 'CANCELED',
  draft: 'DRAFT',
  finished: 'FINISHED',
  paused: 'PAUSED',
  processing: 'PROCESSING',
  scheduled: 'SCHEDULED',
};

type CampaignListFilterParams = Pick<
  CampaignManagementApi.ListCampaignsParams,
  'code' | 'incentive_id' | 'name' | 'status'
>;

export function mapApiCampaignStatus(
  status: CampaignManagementApi.CampaignListStatus,
): CampaignStatus {
  return STATUS_FROM_API[status];
}

export function mapCampaignDetailKpis(
  summary: CampaignManagementApi.CampaignDetailSummary,
): DetailKpi[] {
  return [
    {
      label: 'Parceiros',
      sub: 'parceiros na campanha',
      value: formatNumber(summary.targets_count),
    },
    {
      label: 'Elegíveis',
      sub: `de ${formatNumber(summary.targets_count)} parceiros`,
      value: formatNumber(summary.enrolled_count),
    },
    {
      accent: 'accent',
      label: 'Revalidados',
      sub: 'no período',
      value: formatNumber(summary.revalidated_count),
    },
    {
      accent: 'info',
      label: '% Revalidado',
      sub: 'revalidados / elegíveis',
      value: formatPct(summary.revalidation_rate),
    },
  ];
}

export function mapCampaignDetailResponse(data: CampaignManagementApi.CampaignDetailData) {
  return {
    campaign: {
      activatedAt: formatCampaignDate(data.activated_at),
      activatedBy: data.activated_by,
      code: data.code,
      createdAt: formatCampaignDate(data.created_at),
      createdBy: data.created_by,
      daysActive: data.days_active,
      descriptionPtbr: data.description_ptbr,
      id: data.id,
      incentiveName: data.incentive.name,
      internalDescription: data.internal_description,
      name: data.name,
      status: STATUS_FROM_API[data.status],
    } satisfies CampaignDetail,
    kpis: mapCampaignDetailKpis(data.summary),
  };
}

export function mapCampaignFiltersToApiParams(
  filters: CampaignFilters,
): CampaignListFilterParams {
  const query = filters.search.trim();
  const searchParams = !query
    ? {}
    : /^CAM-/i.test(query)
      ? { code: query }
      : { name: query };

  return {
    ...searchParams,
    ...(filters.status ? { status: STATUS_TO_API[filters.status] } : {}),
    ...(filters.incentiveId ? { incentive_id: filters.incentiveId } : {}),
  };
}

export function mapCampaignItem(item: CampaignManagementApi.CampaignListItem): Campaign {
  return {
    code: item.code,
    createdBy: item.created_by,
    eligibleCount: item.enrolled_count ?? null,
    id: item.id,
    incentiveType: item.incentive?.name ?? '—',
    name: item.name,
    partnersCount: item.target_count,
    revalidatedCount: item.revalidated_count,
    revalidatedPct: item.revalidation_rate ?? null,
    status: STATUS_FROM_API[item.status],
  };
}

export function mapCampaignListResponse(data: CampaignManagementApi.ListCampaignsData) {
  return {
    campaigns: data.items.map(mapCampaignItem),
    kpis: data.summary ? mapCampaignSummary(data.summary) : null,
    page: data.page,
    pages: data.pages,
    total: data.total,
  };
}

export function mapCampaignSummary(
  summary: CampaignManagementApi.CampaignListSummary,
): CampaignKpis {
  return {
    activeCampaigns: summary.total_active_campaigns,
    eligibleCcbs: summary.total_active_projects,
    revalidationRate: summary.revalidation_rate,
    totalPartners: summary.total_active_partners,
  };
}

export function mapCreateCampaignPayload(
  identification: IdentificationData,
  period: PeriodData,
  segmentation: SegmentationData,
  incentiveDescription?: string,
): CampaignManagementApi.CreateCampaignRequest {
  const payload: CampaignManagementApi.CreateCampaignRequest = {
    campaign_type: 'REVALIDATION',
    description_ptbr: incentiveDescription?.trim() || identification.name.trim(),
    ends_at: toCampaignPeriodBoundaryIso(period.endDate, 'end'),
    incentive_id: identification.incentive,
    name: identification.name.trim(),
    starts_at: toCampaignPeriodBoundaryIso(period.startDate, 'start'),
    targets: segmentation.partnerIds.map((partnerId) => ({
      target_id: partnerId,
      target_type: 'PARTNER',
    })),
  };

  const hypothesis = identification.hypothesis.trim();
  if (hypothesis) {
    payload.internal_description = hypothesis;
  }

  if (segmentation.ccbMin != null) {
    payload.min_projects_per_target = segmentation.ccbMin;
  }

  if (segmentation.ccbMaxPerClient != null) {
    payload.max_projects_per_target = segmentation.ccbMaxPerClient;
  }

  if (segmentation.brands.length > 0) {
    payload.eligible_brands = segmentation.brands;
  }

  if (segmentation.delayMin != null) {
    payload.min_delay_days = segmentation.delayMin;
  }

  if (segmentation.delayMax != null) {
    payload.max_delay_days = segmentation.delayMax;
  }

  return payload;
}

export function mapIncentiveSelectOptions(
  items: CampaignManagementApi.IncentiveListItem[],
): FilterSelectOption[] {
  return items.map((item) => ({
    description: item.description_ptbr || undefined,
    id: item.id,
    label: item.description_ptbr
      ? `${item.name} — ${item.description_ptbr}`
      : item.name,
  }));
}
